import { Controller } from "@hotwired/stimulus"

// Controla recursos interativos do formulário de NotificacaoConfiguracao:
// 1. Auto-geração inteligente do Código (slug) a partir do Nome Amigável
// 2. Inserção interativa de tags {{variavel}} no campo em foco
// 3. Construtor visual de regras de filtro (gatilho_condicao)
// 4. Live Preview dinâmico do balão no sininho e popup de navegador em tempo real
export default class extends Controller {
  static targets = [
    "previewContainer",
    "previewBadge",
    "previewBadgeMini",
    "previewStatus",
    "previewTitulo",
    "previewMensagem",
    "previewDestino",
    "previewGatilho",
    "previewStatusAtivo",
    "previewWebPush",
    "previewWebPushBox",
    "previewToastTitulo",
    "previewToastMensagem",
    "previewUrlPill",
    "previewUrlText",
    "manualCode"
  ]

  connect() {
    this.abortController = new AbortController()
    const signal = this.abortController.signal

    this.lastFocusedInput = null
    this.lastStateHash = ""
    this.lastAddTimestamp = 0
    this.trackedInputs = new WeakSet()

    this.setupListeners(signal)
    this.setupAutoSlug()
    this.setupTomSelectHooks()
    this.sincronizarModoGatilho(false)
    this.initFilterBuilder()
    this.atualizarPreview()
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  // -------------------------------------------------------------
  // Listeners com AbortSignal (Cleanup Seguro)
  // -------------------------------------------------------------

  setupListeners(signal) {
    const updateHandler = (event) => {
      if (event?.type === "change" && event.target === this.gatilhoModeloSelect) {
        this.sincronizarModoGatilho(true)
      }
      this.atualizarPreview()
    }

    // Escuta eventos de input, change, keyup e paste no elemento raiz
    this.element.addEventListener("input", updateHandler, { signal })
    this.element.addEventListener("change", updateHandler, { signal })
    this.element.addEventListener("keyup", updateHandler, { signal })
    this.element.addEventListener("paste", () => setTimeout(updateHandler, 20), { signal })

    // Observer para capturar quando dependent frames recarregam o HTML do filtro
    const mutationObserver = new MutationObserver((mutations) => {
      const dependentFieldChanged = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.id === "gatilho_condicao_field_wrapper" ||
            node.querySelector?.("#gatilho_condicao_field_wrapper"))
        )
      )
      if (!dependentFieldChanged) return

      this.initFilterBuilder()
      this.atualizarPreview()
    })
    mutationObserver.observe(this.element, { childList: true, subtree: true })
    signal.addEventListener("abort", () => mutationObserver.disconnect())

    // Listener global com signal para tags, botões de filtro e dropdowns
    document.addEventListener(
      "click",
      (e) => {
        // Inserção de tags do dicionário
        const tagBtn = e.target.closest("[data-tag]")
        if (tagBtn) {
          e.preventDefault()
          e.stopPropagation()
          this.inserirTag(tagBtn)
          return
        }

        // Adicionar regra no construtor de filtros
        const btnAdd = e.target.closest(".notif-btn-add-rule, #btn_notif_add_rule, #btn_notif_add_first_rule")
        if (btnAdd) {
          e.preventDefault()
          e.stopPropagation()
          const wrapper = btnAdd.closest("#notif_filter_builder_wrapper") || document.getElementById("notif_filter_builder_wrapper")
          this.createFilterRow(wrapper)
          return
        }

        // Remover regra do construtor de filtros
        const btnRemove = e.target.closest(".btn-remove-filter-row, .btn-remove-row")
        if (btnRemove) {
          e.preventDefault()
          e.stopPropagation()
          const row = btnRemove.closest(".notif-filter-row")
          if (row) {
            const wrapper = row.closest("#notif_filter_builder_wrapper") || document.getElementById("notif_filter_builder_wrapper")
            row.remove()
            this.syncFilterRows(wrapper)
          }
          return
        }

        if (
          e.target.closest(".ts-dropdown") ||
          e.target.closest(".ts-control") ||
          e.target.closest(".form-check") ||
          e.target.closest(".btn")
        ) {
          setTimeout(updateHandler, 30)
        }
      },
      { signal }
    )

    // Rastreia foco e posição do cursor em todos os inputs de texto
    const trackInput = (inp) => {
      if (!inp) return
      const isRelevant = inp.name?.includes("template") || inp.name?.includes("nome") || inp.name?.includes("url")
      if (!isRelevant || this.trackedInputs.has(inp)) return
      this.trackedInputs.add(inp)

      const updateCursor = () => {
        this.lastFocusedInput = inp
        if (typeof inp.selectionStart === "number") {
          this.lastSelectionStart = inp.selectionStart
          this.lastSelectionEnd = inp.selectionEnd
        }
      }

      inp.addEventListener("focus", updateCursor, { signal })
      inp.addEventListener("click", updateCursor, { signal })
      inp.addEventListener("keyup", updateCursor, { signal })
      inp.addEventListener("select", updateCursor, { signal })
      inp.addEventListener("blur", updateCursor, { signal })
    }

    this.element.querySelectorAll("input, textarea").forEach(trackInput)

    document.addEventListener(
      "focusin",
      (e) => {
        const inp = e.target
        if (inp && (inp.name?.includes("template") || inp.name?.includes("nome") || inp.name?.includes("url"))) {
          trackInput(inp)
        }
      },
      { signal }
    )
  }

  setupTomSelectHooks() {
    const selects = [this.tipoSelect, this.destinoTipoSelect, this.gatilhoModeloSelect, this.gatilhoEventoSelect]
    selects.forEach((sel) => {
      if (!sel) return
      if (sel.tomselect && typeof sel.tomselect.on === "function") {
        sel.tomselect.on("change", () => {
          this.atualizarPreview()
        })
      }
    })
  }

  sincronizarModoGatilho(modeloMudou) {
    const modelo = this.getValorSelect(this.gatilhoModeloSelect, "")
    const evento = this.getValorSelect(this.gatilhoEventoSelect, "after_create")

    if (!modelo && evento !== "manual") {
      this.setSelectValue(this.gatilhoEventoSelect, "manual")
    } else if (modeloMudou && modelo && evento === "manual") {
      this.setSelectValue(this.gatilhoEventoSelect, "after_create")
    }

    this.setGatilhoEventoLocked(!modelo)
  }

  setSelectValue(el, value) {
    if (!el) return
    if (el.tomselect && typeof el.tomselect.setValue === "function") {
      el.tomselect.setValue(value, true)
    } else {
      el.value = value
    }
  }

  setGatilhoEventoLocked(locked) {
    const select = this.gatilhoEventoSelect
    if (!select) return

    if (select.tomselect) {
      const method = locked ? "lock" : "unlock"
      if (typeof select.tomselect[method] === "function") select.tomselect[method]()
      select.tomselect.wrapper?.classList.toggle("opacity-75", locked)
      select.tomselect.wrapper?.setAttribute("aria-disabled", locked ? "true" : "false")
    } else {
      select.style.pointerEvents = locked ? "none" : ""
      select.tabIndex = locked ? -1 : 0
      select.classList.toggle("opacity-75", locked)
      select.setAttribute("aria-disabled", locked ? "true" : "false")
    }
  }

  setupAutoSlug() {
    const nome = this.nomeInput
    const codigo = this.codigoInput
    if (!nome || !codigo) return

    this.slugModificadoManualmente = Boolean(codigo.value && codigo.value.trim().length > 0)

    codigo.addEventListener("input", () => {
      if (!codigo.value || codigo.value.trim().length === 0) {
        this.slugModificadoManualmente = false
        if (nome.value) {
          codigo.value = this.slugify(nome.value)
        }
      } else {
        this.slugModificadoManualmente = true
      }
    })

    nome.addEventListener("input", () => {
      if (!this.slugModificadoManualmente) {
        codigo.value = this.slugify(nome.value)
      }
    })
  }

  slugify(text) {
    if (!text) return ""
    return text
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/_+/g, "_")
  }

  // -------------------------------------------------------------
  // Construtor Visual de Filtros (Gatilho Condição)
  // -------------------------------------------------------------

  initFilterBuilder() {
    const wrapper = document.getElementById("notif_filter_builder_wrapper")
    if (!wrapper || wrapper.dataset.initialized === "true") return
    wrapper.dataset.initialized = "true"

    const initialCondicao = wrapper.dataset.initial || ""
    if (initialCondicao.trim()) {
      const rules = initialCondicao.split(",").map((r) => r.trim()).filter(Boolean)
      rules.forEach((rule) => {
        let field = ""
        let op = "="
        let val = ""

        if (rule.includes("!=")) {
          const parts = rule.split("!=")
          field = parts[0].trim()
          op = "!="
          val = parts[1].trim()
        } else if (/\sLIKE\s/i.test(rule)) {
          const parts = rule.split(/\sLIKE\s/i)
          field = parts[0].trim()
          op = "LIKE"
          val = parts[1].trim()
        } else if (rule.includes(":")) {
          const parts = rule.split(":")
          field = parts[0].trim()
          val = parts[1].trim()
          if (val === "null") {
            op = "IS NULL"
            val = ""
          } else if (val === "not_null") {
            op = "IS NOT NULL"
            val = ""
          } else {
            op = "="
          }
        } else if (rule.includes(">")) {
          const parts = rule.split(">")
          field = parts[0].trim()
          op = ">"
          val = parts[1].trim()
        } else if (rule.includes("<")) {
          const parts = rule.split("<")
          field = parts[0].trim()
          op = "<"
          val = parts[1].trim()
        }

        if (field) {
          this.createFilterRow(wrapper, field, op, val)
        }
      })
    }

    this.syncFilterRows(wrapper)
  }

  createFilterRow(targetWrapper, fieldVal = "", opVal = "=", valVal = "") {
    const now = Date.now()
    if (!fieldVal && now - this.lastAddTimestamp < 150) return
    if (!fieldVal) this.lastAddTimestamp = now

    const wrapper = targetWrapper || document.getElementById("notif_filter_builder_wrapper")
    if (!wrapper) return

    const container = wrapper.querySelector("#notif_filter_rows_container")
    if (!container) return

    let columns = []
    try {
      columns = JSON.parse(wrapper.dataset.columns || "[]")
    } catch (e) {
      columns = []
    }
    if (columns.length === 0) return

    const selectedFieldId = fieldVal || columns[0]?.id || ""
    const selectedCol = columns.find((c) => c.id === selectedFieldId) || columns[0]

    const row = document.createElement("div")
    row.className = "notif-filter-row border rounded-3 p-2 bg-body-tertiary d-flex flex-wrap align-items-center gap-2 shadow-xs"

    const colOptionsHtml = columns
      .map((col) => {
        const sel = col.id === selectedFieldId ? "selected" : ""
        return `<option value="${this.escapeHtml(col.id)}" ${sel}>${this.escapeHtml(col.label)}</option>`
      })
      .join("")

    const operators = [
      { id: "=", label: "é igual a (=)" },
      { id: "!=", label: "é diferente de (!=)" },
      { id: "LIKE", label: "contém texto" },
      { id: ">", label: "é maior que (>)" },
      { id: "<", label: "é menor que (<)" },
      { id: "IS NOT NULL", label: "está preenchido (não vazio)" },
      { id: "IS NULL", label: "está vazio (nulo)" }
    ]

    const opOptionsHtml = operators
      .map((o) => {
        const sel = o.id === opVal ? "selected" : ""
        return `<option value="${this.escapeHtml(o.id)}" ${sel}>${this.escapeHtml(o.label)}</option>`
      })
      .join("")

    row.innerHTML = `
      <div class="d-flex align-items-center gap-1" style="min-width: 180px; flex: 1 1 200px;">
        <span class="fs-nano text-muted fw-bold px-1">QUANDO</span>
        <select class="form-select form-select-sm field-select fw-semibold text-dark">
          ${colOptionsHtml}
        </select>
      </div>

      <div style="min-width: 140px; flex: 1 1 150px;">
        <select class="form-select form-select-sm op-select text-secondary">
          ${opOptionsHtml}
        </select>
      </div>

      <div class="val-container" style="min-width: 180px; flex: 2 1 220px;">
        <!-- Dropdown ou Input inserido dinamicamente -->
      </div>

      <button type="button" class="btn btn-xs btn-outline-danger btn-remove-filter-row p-1 px-2" title="Remover esta regra">
        <i class="fa fa-trash"></i>
      </button>
    `

    container.appendChild(row)

    const fieldSelect = row.querySelector(".field-select")
    const opSelect = row.querySelector(".op-select")
    const valContainer = row.querySelector(".val-container")

    const renderValueControl = (col, initialVal) => {
      if (!valContainer) return
      const isNullOp = opSelect.value === "IS NULL" || opSelect.value === "IS NOT NULL"
      if (isNullOp) {
        valContainer.innerHTML = `<span class="text-muted fs-xs fst-italic px-1">(Não requer valor)</span>`
        return
      }

      if (col && Array.isArray(col.values) && col.values.length > 0) {
        let currentChoice = initialVal
        if (!currentChoice && col.values[0]) {
          currentChoice = typeof col.values[0] === "object" ? col.values[0].value : col.values[0]
        }

        const isKnown = col.values.some((v) => String(typeof v === "object" ? v.value : v) === String(currentChoice))
        const isCustom = Boolean(currentChoice && !isKnown)

        let options = col.values
          .map((v) => {
            const valStr = typeof v === "object" ? v.value : v
            const lblStr = typeof v === "object" ? v.label : v
            const isSel = !isCustom && String(valStr) === String(currentChoice) ? "selected" : ""
            return `<option value="${this.escapeHtml(valStr)}" ${isSel}>${this.escapeHtml(lblStr)}</option>`
          })
          .join("")

        options += `<option value="__custom__" ${isCustom ? "selected" : ""}>✏️ Outro valor (digitar)...</option>`

        valContainer.innerHTML = `
          <select class="form-select form-select-sm val-select fw-medium text-dark">
            ${options}
          </select>
          <input type="text" class="form-control form-control-sm val-custom-input mt-1 ${isCustom ? "" : "d-none"}"
                 placeholder="Digite o valor desejado..." value="${isCustom ? this.escapeHtml(currentChoice) : ""}">
        `

        const selectEl = valContainer.querySelector(".val-select")
        const customInp = valContainer.querySelector(".val-custom-input")

        selectEl.addEventListener("change", () => {
          if (selectEl.value === "__custom__") {
            customInp.classList.remove("d-none")
            customInp.focus()
          } else {
            customInp.classList.add("d-none")
            customInp.value = ""
          }
          this.syncFilterRows(wrapper)
        })

        customInp.addEventListener("input", () => this.syncFilterRows(wrapper))
        customInp.addEventListener("change", () => this.syncFilterRows(wrapper))
      } else {
        valContainer.innerHTML = `
          <input type="text" class="form-control form-control-sm val-input"
                 placeholder="Digite o valor desejado (ex: 100)..." value="${this.escapeHtml(initialVal || "")}">
        `
        const inputEl = valContainer.querySelector(".val-input")
        inputEl.addEventListener("input", () => this.syncFilterRows(wrapper))
        inputEl.addEventListener("change", () => this.syncFilterRows(wrapper))
      }
    }

    fieldSelect.addEventListener("change", () => {
      const newCol = columns.find((c) => c.id === fieldSelect.value) || columns[0]
      renderValueControl(newCol, "")
      this.syncFilterRows(wrapper)
    })

    opSelect.addEventListener("change", () => {
      const curCol = columns.find((c) => c.id === fieldSelect.value) || columns[0]
      const curVal = this.getFilterRowVal(row)
      renderValueControl(curCol, curVal)
      this.syncFilterRows(wrapper)
    })

    renderValueControl(selectedCol, valVal)
    this.syncFilterRows(wrapper)
  }

  getFilterRowVal(row) {
    const valSelect = row.querySelector(".val-select")
    const valCustomInput = row.querySelector(".val-custom-input")
    const valInput = row.querySelector(".val-input")

    if (valSelect) {
      if (valSelect.value === "__custom__") {
        return valCustomInput?.value?.trim() || ""
      }
      return valSelect.value || ""
    }
    if (valInput) {
      return valInput.value?.trim() || ""
    }
    return ""
  }

  syncFilterRows(targetWrapper) {
    const wrapper = targetWrapper || document.getElementById("notif_filter_builder_wrapper")
    if (!wrapper) return

    const container = wrapper.querySelector("#notif_filter_rows_container")
    const emptyState = wrapper.querySelector("#notif_filter_empty_state")
    const hiddenInput =
      wrapper.querySelector("#notificacao_configuracao_gatilho_condicao") ||
      document.getElementById("notificacao_configuracao_gatilho_condicao")

    if (!container || !hiddenInput) return

    const rows = container.querySelectorAll(".notif-filter-row")
    if (rows.length === 0) {
      hiddenInput.value = ""
      if (emptyState) emptyState.classList.remove("d-none")
    } else {
      if (emptyState) emptyState.classList.add("d-none")
      const parts = []
      rows.forEach((row) => {
        const field = row.querySelector(".field-select")?.value
        const op = row.querySelector(".op-select")?.value || "="
        const val = this.getFilterRowVal(row)

        if (!field) return

        if (op === "IS NULL") {
          parts.push(`${field}: null`)
        } else if (op === "IS NOT NULL") {
          parts.push(`${field}: not_null`)
        } else if (val) {
          if (op === "=") {
            parts.push(`${field}: ${val}`)
          } else if (op === "!=") {
            parts.push(`${field} != ${val}`)
          } else {
            parts.push(`${field} ${op} ${val}`)
          }
        }
      })
      hiddenInput.value = parts.join(", ")
    }

    hiddenInput.dispatchEvent(new Event("input", { bubbles: true }))
    hiddenInput.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // -------------------------------------------------------------
  // Inserção de Tags no Input Focado
  // -------------------------------------------------------------

  inserirTag(btn) {
    const tag = btn?.dataset?.tag || btn?.getAttribute("data-tag")
    if (!tag) return

    let targetInput = this.lastFocusedInput
    if (!targetInput || !document.body.contains(targetInput)) {
      if (this.mensagemInput && this.tituloInput?.value?.trim()) {
        targetInput = this.mensagemInput
      } else {
        targetInput = this.tituloInput || this.mensagemInput || this.urlInput
      }
    }

    if (!targetInput) return

    const val = targetInput.value || ""
    const start =
      typeof this.lastSelectionStart === "number" && this.lastFocusedInput === targetInput
        ? this.lastSelectionStart
        : typeof targetInput.selectionStart === "number"
        ? targetInput.selectionStart
        : val.length
    const end =
      typeof this.lastSelectionEnd === "number" && this.lastFocusedInput === targetInput
        ? this.lastSelectionEnd
        : typeof targetInput.selectionEnd === "number"
        ? targetInput.selectionEnd
        : val.length

    const prefix = start > 0 && !/\s$/.test(val.substring(0, start)) && !val.substring(0, start).endsWith("{") ? " " : ""
    const textToInsert = prefix + tag
    const newText = val.substring(0, start) + textToInsert + val.substring(end)
    targetInput.value = newText

    const newPos = start + textToInsert.length
    targetInput.focus()
    if (typeof targetInput.setSelectionRange === "function") {
      targetInput.setSelectionRange(newPos, newPos)
    }

    this.lastFocusedInput = targetInput
    this.lastSelectionStart = newPos
    this.lastSelectionEnd = newPos

    targetInput.dispatchEvent(new Event("input", { bubbles: true }))
    targetInput.dispatchEvent(new Event("change", { bubbles: true }))

    // Feedback visual
    if (btn && btn.classList) {
      btn.classList.add("btn-primary", "text-white")
      btn.classList.remove("btn-outline-secondary")
      setTimeout(() => {
        btn.classList.remove("btn-primary", "text-white")
        btn.classList.add("btn-outline-secondary")
      }, 250)
    }

    this.atualizarPreview()
  }

  // -------------------------------------------------------------
  // Getters de Elementos
  // -------------------------------------------------------------

  get nomeInput() {
    return (
      this.element.querySelector('input[name*="[nome]"]') ||
      document.querySelector('input[name="notificacao_configuracao[nome]"]') ||
      document.getElementById("notificacao_configuracao_nome")
    )
  }

  get codigoInput() {
    return (
      this.element.querySelector('input[name*="[codigo]"]') ||
      document.querySelector('input[name="notificacao_configuracao[codigo]"]') ||
      document.getElementById("notificacao_configuracao_codigo")
    )
  }

  get tipoSelect() {
    return (
      this.element.querySelector('select[name*="[tipo]"]') ||
      document.querySelector('select[name="notificacao_configuracao[tipo]"]') ||
      document.getElementById("notificacao_configuracao_tipo")
    )
  }

  get destinoTipoSelect() {
    return (
      this.element.querySelector('select[name*="[destino_tipo]"]') ||
      document.querySelector('select[name="notificacao_configuracao[destino_tipo]"]') ||
      document.getElementById("notificacao_configuracao_destino_tipo")
    )
  }

  get gatilhoModeloSelect() {
    return (
      this.element.querySelector('select[name*="[gatilho_modelo]"]') ||
      document.querySelector('select[name="notificacao_configuracao[gatilho_modelo]"]') ||
      document.getElementById("notificacao_configuracao_gatilho_modelo")
    )
  }

  get gatilhoEventoSelect() {
    return (
      this.element.querySelector('select[name*="[gatilho_evento]"]') ||
      document.querySelector('select[name="notificacao_configuracao[gatilho_evento]"]') ||
      document.getElementById("notificacao_configuracao_gatilho_evento")
    )
  }

  get gatilhoCondicaoInput() {
    return (
      this.element.querySelector('input[name*="[gatilho_condicao]"]') ||
      document.querySelector('input[name="notificacao_configuracao[gatilho_condicao]"]') ||
      document.getElementById("notificacao_configuracao_gatilho_condicao")
    )
  }

  get tituloInput() {
    return (
      this.element.querySelector('input[name*="[titulo_template]"]') ||
      document.querySelector('input[name="notificacao_configuracao[titulo_template]"]') ||
      document.getElementById("notificacao_configuracao_titulo_template")
    )
  }

  get mensagemInput() {
    return (
      this.element.querySelector('textarea[name*="[mensagem_template]"]') ||
      document.querySelector('textarea[name="notificacao_configuracao[mensagem_template]"]') ||
      document.getElementById("notificacao_configuracao_mensagem_template")
    )
  }

  get urlInput() {
    return (
      this.element.querySelector('input[name*="[url_template]"]') ||
      document.querySelector('input[name="notificacao_configuracao[url_template]"]') ||
      document.getElementById("notificacao_configuracao_url_template")
    )
  }

  get ativoCheckbox() {
    return (
      this.element.querySelector('input[name*="[ativo]"][type="checkbox"]') ||
      document.querySelector('input[name="notificacao_configuracao[ativo]"][type="checkbox"]') ||
      document.getElementById("notificacao_configuracao_ativo")
    )
  }

  get dispararNavegadorCheckbox() {
    return (
      this.element.querySelector('input[name*="[disparar_navegador]"][type="checkbox"]') ||
      document.querySelector('input[name="notificacao_configuracao[disparar_navegador]"][type="checkbox"]') ||
      document.getElementById("notificacao_configuracao_disparar_navegador")
    )
  }

  getValorSelect(el, defaultVal = "") {
    if (!el) return defaultVal
    if (el.tomselect && typeof el.tomselect.getValue === "function") {
      const val = el.tomselect.getValue()
      if (val !== undefined && val !== null && String(val).trim().length > 0) {
        return String(val).trim()
      }
    }
    return String(el.value || defaultVal).trim()
  }

  // -------------------------------------------------------------
  // Atualização Central do Preview
  // -------------------------------------------------------------

  atualizarPreview() {
    const tipo = this.getValorSelect(this.tipoSelect, "sistema").toLowerCase() || "sistema"
    const rawTitulo = this.tituloInput?.value?.trim() || ""
    const rawNome = this.nomeInput?.value?.trim() || ""
    const rawMensagem = this.mensagemInput?.value?.trim() || ""
    const rawUrl = this.urlInput?.value?.trim() || ""
    const ativo = this.ativoCheckbox ? this.ativoCheckbox.checked : true
    const dispararNavegador = this.dispararNavegadorCheckbox ? this.dispararNavegadorCheckbox.checked : false
    const destinoTipo = this.getValorSelect(this.destinoTipoSelect, "admins")
    const modelo = this.getValorSelect(this.gatilhoModeloSelect, "")
    const evento = this.getValorSelect(this.gatilhoEventoSelect, "after_create")
    const condicao = this.gatilhoCondicaoInput?.value?.trim() || ""

    const cor = this.corParaTipo(tipo)
    const labelTipo = this.labelParaTipo(tipo)

    const previewItem = this.findTarget("previewContainer")?.querySelector(".notif-item")
    if (previewItem) previewItem.dataset.notifTipo = tipo

    // 1. Badges de Categoria
    this.updateElement(this.findTarget("previewBadge"), (el) => {
      el.className = `badge bg-${cor}-subtle text-${cor} border border-${cor}-subtle text-uppercase fs-nano px-2 py-0 fw-semibold`
      el.textContent = labelTipo
    })

    this.updateElement(this.findTarget("previewBadgeMini"), (el) => {
      el.className = `badge bg-${cor}-subtle text-${cor} border border-${cor}-subtle text-uppercase fs-nano px-1 py-0 fw-semibold`
      el.textContent = labelTipo
    })

    // 2. Bullet de Status
    this.updateElement(this.findTarget("previewStatus"), (el) => {
      el.className = `status status-${cor} me-2 mt-1 flex-shrink-0`
    })

    // 3. Interpolações
    const tituloInterpolado = rawTitulo ? this.interpolarExemplo(rawTitulo) : rawNome ? rawNome : "Título da notificação"
    const mensagemInterpolada = rawMensagem ? this.interpolarExemplo(rawMensagem) : "Corpo descritivo da mensagem..."
    const urlInterpolada = rawUrl ? this.interpolarExemplo(rawUrl) : ""

    // 4. Título no Balão
    this.updateElement(this.findTarget("previewTitulo"), (el) => {
      if (urlInterpolada) {
        el.innerHTML = `<a href="javascript:void(0)" class="text-primary text-decoration-none fw-semibold">${this.escapeHtml(
          tituloInterpolado
        )} <i class="fa fa-external-link-alt fs-nano ms-1 opacity-75"></i></a>`
      } else {
        el.textContent = tituloInterpolado
      }
    })

    // 5. Mensagem no Balão
    this.updateElement(this.findTarget("previewMensagem"), (el) => {
      el.textContent = mensagemInterpolada
      if (!rawMensagem) {
        el.classList.add("text-muted", "fst-italic")
        el.classList.remove("text-secondary")
      } else {
        el.classList.remove("text-muted", "fst-italic")
        el.classList.add("text-secondary")
      }
    })

    // 6. Link Pill
    const urlPillEl = this.findTarget("previewUrlPill")
    const urlTextEl = this.findTarget("previewUrlText")
    if (urlPillEl) {
      if (urlInterpolada) {
        urlPillEl.classList.remove("d-none")
        if (urlTextEl) urlTextEl.textContent = urlInterpolada
      } else {
        urlPillEl.classList.add("d-none")
      }
    }

    // 7. Destinatários
    this.updateElement(this.findTarget("previewDestino"), (el) => {
      el.textContent = this.labelParaDestino(destinoTipo)
    })

    // 8. Status Ativo / Inativo
    this.updateElement(this.findTarget("previewStatusAtivo"), (el) => {
      if (ativo) {
        el.className = "badge bg-success-subtle text-success fs-nano px-2 py-0"
        el.textContent = "Ativa"
      } else {
        el.className = "badge bg-danger-subtle text-danger fs-nano px-2 py-0"
        el.textContent = "Inativa"
      }
    })

    // Opacidade do container geral
    this.updateElement(this.findTarget("previewContainer"), (el) => {
      el.style.opacity = ativo ? "1" : "0.75"
    })

    // 9. Web Push / Desktop Notification
    this.updateElement(this.findTarget("previewWebPush"), (el) => {
      if (dispararNavegador) {
        el.className = "badge bg-info-subtle text-info fs-nano px-2 py-0"
        el.textContent = "Popup do navegador ativo"
      } else {
        el.className = "badge bg-secondary-subtle text-secondary fs-nano px-2 py-0"
        el.textContent = "Sem popup"
      }
    })

    const webPushBoxEl = this.findTarget("previewWebPushBox")
    if (webPushBoxEl) {
      if (dispararNavegador) {
        webPushBoxEl.classList.remove("d-none")
        this.updateElement(this.findTarget("previewToastTitulo"), (el) => {
          el.textContent = tituloInterpolado
        })
        this.updateElement(this.findTarget("previewToastMensagem"), (el) => {
          el.textContent = mensagemInterpolada
        })
      } else {
        webPushBoxEl.classList.add("d-none")
      }
    }

    // 10. Gatilho
    this.updateElement(this.findTarget("previewGatilho"), (el) => {
      if (modelo) {
        const eventoFormatado = this.labelParaEvento(evento)
        const condicaoTexto = condicao ? ` (${condicao})` : ""
        el.innerHTML = `<i class="fa fa-bolt text-warning me-1"></i> Disparo: <strong>${this.escapeHtml(
          modelo
        )}</strong> → ${this.escapeHtml(eventoFormatado)}${this.escapeHtml(condicaoTexto)}`
      } else {
        el.innerHTML = `<i class="fa fa-hand-paper-o text-muted me-1"></i> Disparo Manual via Código`
      }
    })

    this.atualizarCodigoManual()
  }

  atualizarCodigoManual() {
    const codeEl = this.findTarget("manualCode")
    if (!codeEl) return

    const codigo = this.codigoInput?.value?.trim() || "codigo_da_notificacao"
    const templates = [this.tituloInput?.value, this.mensagemInput?.value, this.urlInput?.value]
      .filter(Boolean)
      .join(" ")
    const tags = [...new Set(Array.from(templates.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g), (match) => match[1]))]
    const params = tags.length > 0
      ? `\n  params: {\n${tags.map((tag) => `    ${tag}: "Exemplo"`).join(",\n")}\n  },`
      : "\n  params: {},"

    codeEl.textContent = `Notificacoes::Broadcaster.notificar(\n  ${JSON.stringify(codigo)},${params}\n  autor_id: usuario_id\n)`
  }

  async copiarCodigoManual(event) {
    const codeEl = this.findTarget("manualCode")
    if (!codeEl || !navigator.clipboard) return

    await navigator.clipboard.writeText(codeEl.textContent)
    const button = event.currentTarget
    const originalTitle = button.title
    button.title = "Código copiado"
    button.classList.add("text-success")
    setTimeout(() => {
      button.title = originalTitle
      button.classList.remove("text-success")
    }, 1500)
  }

  interpolarExemplo(template) {
    if (!template) return ""
    const rawNome = this.nomeInput?.value?.trim() || "Campanha de Fidelização"

    const amostras = {
      nome: rawNome,
      titulo: rawNome,
      id: "1042",
      total: "150",
      status: "Aprovado",
      autor: "Administrador",
      socio: "Carlos Silva",
      pessoa: "Carlos Silva",
      motivo: "Adesão regularizada",
      valor: "R$ 150,00",
      data: new Date().toLocaleDateString("pt-BR"),
      hora: new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
      plano: "Sócio Torcedor Ouro",
      campanha_id: "1042",
      usuario: "João Santos",
      email: "contato@empresa.com",
      url: "/campanhas/1042"
    }

    return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) => {
      if (amostras[key] !== undefined) {
        return amostras[key]
      }
      return match
    })
  }

  // -------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------

  findTarget(name) {
    return (
      this.element.querySelector(`[data-notificacao-config-form-target="${name}"]`) ||
      document.querySelector(`[data-notificacao-config-form-target="${name}"]`)
    )
  }

  updateElement(el, callback) {
    if (el && typeof callback === "function") {
      callback(el)
    }
  }

  corParaTipo(tipo) {
    switch (tipo) {
      case "campanha":
      case "success":
        return "success"
      case "error":
        return "danger"
      case "warning":
        return "warning"
      default:
        return "info"
    }
  }

  labelParaTipo(tipo) {
    switch (tipo) {
      case "campanha":
        return "Campanha"
      case "sistema":
        return "Sistema"
      case "warning":
        return "Alerta"
      case "error":
        return "Erro"
      case "success":
        return "Sucesso"
      case "info":
        return "Informativo"
      default:
        return "Notificação"
    }
  }

  labelParaDestino(destino) {
    switch (destino) {
      case "admins":
        return "🛡️ Admins"
      case "grupo":
        return "👥 Grupos"
      case "autor":
        return "👤 Autor"
      case "autor_e_admins":
        return "👤+🛡️ Autor+Admins"
      case "autor_e_grupo":
        return "👤+👥 Autor+Grupos"
      case "geral":
        return "🌐 Geral (Todos)"
      default:
        return "🛡️ Admins"
    }
  }

  labelParaEvento(evento) {
    switch (evento) {
      case "after_create":
        return "Ao Criar"
      case "after_update":
        return "Ao Atualizar"
      case "status_change":
        return "Ao Mudar Status"
      case "after_destroy":
        return "Ao Excluir"
      case "manual":
        return "Manual"
      default:
        return evento || "Ao Criar"
    }
  }

  escapeHtml(str) {
    if (!str) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
